import React, { useState } from 'react';
import { Button } from './ui/button';
import { Download, Share, Smartphone, X } from 'lucide-react';
import { useInstallPrompt } from '../lib/useInstallPrompt';
import { toast } from 'sonner';

/**
 * Persistent "Install app" affordance shown in the sidebar user footer.
 * Complements the auto-dismissable InstallPrompt banner: even after users
 * hide the banner, they can install any time from here.
 *
 * Hidden entirely when the app is already running as an installed PWA
 * (display-mode: standalone) so the UI stays clean on native.
 */
export const InstallAppButton = () => {
  const { canPromptNatively, isIOS, isStandalone, promptInstall } = useInstallPrompt();
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  if (isStandalone) return null;

  const handleClick = async () => {
    if (isIOS) {
      setShowIOSHelp(true);
      return;
    }
    if (canPromptNatively) {
      const outcome = await promptInstall();
      if (outcome === 'accepted') {
        toast.success('My SPAN is being installed on your device');
      }
      return;
    }
    // Desktop/Android where Chrome hasn't fired beforeinstallprompt yet
    // (e.g., visit criteria not met, or already dismissed at the browser
    // level). Nudge the user to the browser menu.
    toast('Open your browser menu and tap "Install app" to add My SPAN to your home screen.', {
      icon: <Smartphone className="h-4 w-4" />,
      duration: 6000,
    });
  };

  return (
    <>
      <Button
        onClick={handleClick}
        variant="outline"
        className="w-full justify-start gap-2 mb-2 text-[#2563eb] border-[#2563eb]/30 hover:bg-[#2563eb]/5 hover:text-[#1d4ed8]"
        data-testid="sidebar-install-app-button"
      >
        <Download className="h-4 w-4" />
        Install app
      </Button>

      {showIOSHelp && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 flex items-end md:items-center justify-center p-4"
          role="dialog"
          aria-label="How to install My SPAN on iPhone"
          data-testid="ios-install-help"
          onClick={() => setShowIOSHelp(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#2563eb] text-white flex items-center justify-center">
                  <Download className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-base font-semibold" style={{ fontFamily: 'Outfit' }}>
                    Install My SPAN
                  </p>
                  <p className="text-xs text-[#64748b]">Add it to your Home Screen</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowIOSHelp(false)}
                className="text-[#94a3b8] hover:text-[#0f172a] p-1"
                aria-label="Close"
                data-testid="close-ios-install-help"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <ol className="space-y-3 mt-4 text-sm text-[#334155]">
              <li className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-[#f1f5f9] text-[#0f172a] text-xs font-semibold flex items-center justify-center flex-shrink-0">
                  1
                </span>
                <span>
                  Tap the <Share className="inline h-4 w-4 mx-0.5 text-[#2563eb]" /> Share icon at
                  the bottom of Safari.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-[#f1f5f9] text-[#0f172a] text-xs font-semibold flex items-center justify-center flex-shrink-0">
                  2
                </span>
                <span>
                  Scroll down and choose <b>Add to Home Screen</b>.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-[#f1f5f9] text-[#0f172a] text-xs font-semibold flex items-center justify-center flex-shrink-0">
                  3
                </span>
                <span>
                  Tap <b>Add</b> — My SPAN will appear on your Home Screen like a native app.
                </span>
              </li>
            </ol>
            <Button
              onClick={() => setShowIOSHelp(false)}
              className="w-full mt-5 bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
              data-testid="ios-install-help-done"
            >
              Got it
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallAppButton;
